import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Mail, Phone, Eye, AlertCircle, CheckSquare, Square } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import UsageWarning from '../components/UsageWarning';
import { useI18n } from '../contexts/I18nContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contacts = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [error, setError] = useState(null);
  const [availableCustomFields, setAvailableCustomFields] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [dupeGroups, setDupeGroups] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState('');
  const [assignOwnerId, setAssignOwnerId] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    status: 'lead',
    tags: '',
    notes: '',
    custom: {}
  });

  useEffect(() => {
    fetchContacts();
    // load custom fields for contacts (optional - endpoint may not exist)
    (async () => {
      try {
        const res = await axios.get(`${API}/custom-fields`, { params: { entity: 'contact' }, withCredentials: true });
        setAvailableCustomFields(res.data || []);
      } catch (err) {
        // Custom fields endpoint is optional - ignore 404 errors
        if (err.response?.status !== 404) {
          console.log('Custom fields not available:', err.message);
        }
      }
    })();
  }, [searchTerm]);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`, {
        params: { search: searchTerm || undefined, owner_id: ownerFilter || undefined },
        withCredentials: true,
      });
      setContacts(response.data);
      setSelectedIds([]);
      // fetch duplicates in background
      try {
        const d = await axios.get(`${API}/contacts/duplicates`, { withCredentials: true });
        setDupeGroups(d.data || []);
      } catch {}
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === contacts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(contacts.map((c) => c.id));
    }
  };

  const hasSelection = selectedIds.length > 0;

  const importInputRef = React.useRef(null);
  const handleExportCsv = () => {
    window.location.href = `${API}/contacts/export/csv`;
  };
  const handleImportClick = () => importInputRef.current?.click();
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/contacts/bulk/import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data) {
        alert(`Import completed!\nImported: ${response.data.imported}\nFailed: ${response.data.failed}`);
      }
      fetchContacts();
    } catch (err) {
      console.error('Import failed', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Import failed. Please try again.';
      alert(`Import failed: ${errorMsg}`);
    } finally {
      e.target.value = '';
    }
  };

  const mergeDuplicates = async (email, group) => {
    if (group.contacts.length < 2) return;
    const primaryId = group.contacts[0].id;
    const duplicateIds = group.contacts.slice(1).map(c => c.id);
    if (!window.confirm(`Merge ${duplicateIds.length} duplicate(s) into ${group.contacts[0].name || email}?`)) return;
    try {
      await axios.post(`${API}/contacts/merge`, { primary_id: primaryId, duplicate_ids: duplicateIds }, { withCredentials: true });
      fetchContacts();
    } catch (e) {
      console.error('Merge failed', e);
    }
  };

  const bulkDelete = async () => {
    if (!hasSelection) return;
    if (!window.confirm(`Delete ${selectedIds.length} contacts?`)) return;
    try {
      await axios.post(`${API}/contacts/bulk/delete`, selectedIds, { withCredentials: true });
      fetchContacts();
    } catch (e) {
      console.error('Bulk delete failed', e);
    }
  };

  const bulkUpdateStatus = async (status) => {
    if (!hasSelection) return;
    try {
      await axios.post(
        `${API}/contacts/bulk/update`,
        { ids: selectedIds, status },
        { withCredentials: true }
      );
      fetchContacts();
    } catch (e) {
      console.error('Bulk update failed', e);
    }
  };

  const bulkAssignOwner = async () => {
    if (!hasSelection || !assignOwnerId) return;
    try {
      await axios.post(
        `${API}/contacts/bulk/update`,
        { ids: selectedIds, owner_id: assignOwnerId },
        { withCredentials: true }
      );
      setAssignOwnerId('');
      fetchContacts();
    } catch (e) {
      console.error('Bulk assign failed', e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
      };
      await axios.post(`${API}/contacts`, payload, { withCredentials: true });
      setIsDialogOpen(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        status: 'lead',
        tags: '',
        notes: '',
      });
      fetchContacts();
    } catch (error) {
      console.error('Error creating contact:', error);
      if (error.response && error.response.status === 403) {
        const errorData = error.response.data;
        if (typeof errorData.detail === 'object') {
          setError({
            title: errorData.detail.message,
            description: `You've used ${errorData.detail.current_count} of ${errorData.detail.limit} contacts on your ${errorData.detail.plan_name} plan.`,
            suggestion: errorData.detail.upgrade_suggestion
          });
        } else {
          setError({
            title: 'Contact Limit Reached',
            description: errorData.detail || 'You have reached your plan limit.',
            suggestion: 'Please upgrade your plan to add more contacts.'
          });
        }
      } else {
        setError({
          title: 'Error Creating Contact',
          description: 'An unexpected error occurred. Please try again.',
          suggestion: ''
        });
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await axios.delete(`${API}/contacts/${id}`, { withCredentials: true });
        fetchContacts();
      } catch (error) {
        console.error('Error deleting contact:', error);
      }
    }
  };

  const statusColors = {
    lead: 'bg-blue-100 text-blue-800',
    customer: 'bg-green-100 text-green-800',
    partner: 'bg-purple-100 text-purple-800',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0176D3]"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Usage Warning */}
      <UsageWarning resourceType="contacts" />
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('pages.contacts.title','Contacts')}</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">{t('pages.contacts.subtitle','Manage your contacts and relationships')}</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <input ref={importInputRef} type="file" accept=".csv" className="hidden" onChange={handleImportFile} />
          <Button variant="outline" onClick={handleImportClick}>Import CSV</Button>
          <Button variant="outline" onClick={handleExportCsv}>Export CSV</Button>
          {hasSelection && (
            <>
              <Button variant="destructive" onClick={bulkDelete} className="bg-red-600 hover:bg-red-700">
                <Trash2 className="w-4 h-4 mr-2" /> Delete ({selectedIds.length})
              </Button>
              <Button variant="outline" onClick={() => bulkUpdateStatus('lead')}>Set Lead</Button>
              <Button variant="outline" onClick={() => bulkUpdateStatus('customer')}>Set Customer</Button>
              <Button variant="outline" onClick={() => bulkUpdateStatus('partner')}>Set Partner</Button>
            </>
          )}
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0176D3] hover:bg-[#0155A8] text-white w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                {t('pages.contacts.addBtn','Add Contact')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-lg sm:text-xl">Add New Contact</DialogTitle>
              </DialogHeader>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div>
                      <h3 className="text-red-800 font-medium">{error.title}</h3>
                      <p className="text-red-700 text-sm mt-1">{error.description}</p>
                      {error.suggestion && (
                        <p className="text-red-600 text-sm mt-2 font-medium">{error.suggestion}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <div>
                  <Label htmlFor="name" className="text-sm sm:text-base">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="lead">Lead</option>
                    <option value="customer">Customer</option>
                    <option value="partner">Partner</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="tags">Tags (comma separated)</Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="tag1, tag2, tag3"
                  />
                </div>
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows="3"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#0176D3] hover:bg-[#0155A8]">
                    Create Contact
                  </Button>
                </div>
              {/* Render custom fields */}
              {availableCustomFields.length > 0 && (
                <div className="pt-3 border-t">
                  <h4 className="font-medium mb-2">Custom Fields</h4>
                  <div className="grid grid-cols-1 gap-3">
                    {availableCustomFields.map((f) => (
                      <div key={f.id}>
                        <Label>{f.name}</Label>
                        {f.type === 'dropdown' ? (
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            value={formData.custom?.[f.key] || ''}
                            onChange={(e) => setFormData({ ...formData, custom: { ...(formData.custom || {}), [f.key]: e.target.value } })}
                          >
                            <option value="">—</option>
                            {(f.options || []).map((opt, i) => (
                              <option key={i} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : (
                          <Input
                            value={formData.custom?.[f.key] || ''}
                            onChange={(e) => setFormData({ ...formData, custom: { ...(formData.custom || {}), [f.key]: e.target.value } })}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Owner:</span>
              <input
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
                placeholder="owner user id"
                className="border rounded px-2 py-1 text-sm"
              />
              <Button size="sm" variant="outline" onClick={fetchContacts}>Apply</Button>
            </div>

            {hasSelection && (
              <div className="flex items-center gap-2">
                <input
                  value={assignOwnerId}
                  onChange={(e) => setAssignOwnerId(e.target.value)}
                  placeholder="assign owner user id"
                  className="border rounded px-2 py-1 text-sm"
                />
                <Button size="sm" variant="outline" onClick={bulkAssignOwner}>Assign Owner</Button>
              </div>
            )}
          </div>
          {dupeGroups.length > 0 && (
            <div className="p-3 mb-3 rounded-md border border-yellow-300 bg-yellow-50 text-sm">
              <div className="font-medium text-yellow-800 mb-2">Possible duplicates found</div>
              <div className="space-y-2">
                {dupeGroups.slice(0,3).map((g, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="text-yellow-900">
                      <span className="font-mono">{g.email}</span> • {g.count} records
                    </div>
                    <Button size="sm" variant="outline" onClick={() => mergeDuplicates(g.email, g)}>Merge</Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search contacts by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {contacts.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-gray-500 text-lg">{t('pages.contacts.noItems','No contacts found. Create your first contact!')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          {/* Select all */}
          <div className="col-span-full flex items-center gap-2 -mb-2">
            <button onClick={toggleSelectAll} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
              {selectedIds.length === contacts.length && contacts.length > 0 ? (
                <CheckSquare className="w-4 h-4" />
              ) : (
                <Square className="w-4 h-4" />
              )}
              <span>Select all</span>
            </button>
          </div>
          {contacts.map((contact) => (
            <Card key={contact.id} className={`hover:shadow-lg transition-shadow ${selectedIds.includes(contact.id) ? 'ring-2 ring-[#0176D3]' : ''}`}>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div className="min-w-0 flex-1 mr-2">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">{contact.name}</h3>
                    {contact.company && (
                      <p className="text-xs sm:text-sm text-gray-600 truncate">{contact.company}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleSelect(contact.id)} className="text-gray-500 hover:text-gray-800">
                      {selectedIds.includes(contact.id) ? (
                        <CheckSquare className="w-4 h-4" />
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                    </button>
                    <Badge className={`${statusColors[contact.status]} text-xs whitespace-nowrap`}>
                      {contact.status}
                    </Badge>
                  </div>
                </div>

                <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                  {contact.email && (
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                      <Mail className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="truncate">{contact.email}</span>
                    </div>
                  )}
                  {typeof contact.opens_count !== 'undefined' && (
                    <div className="text-xs text-gray-500">Opens: {contact.opens_count}</div>
                  )}
                  {contact.phone && (
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                      <Phone className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="truncate">{contact.phone}</span>
                    </div>
                  )}
                </div>

                {contact.tags && contact.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {contact.tags.map((tag, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="flex gap-1.5 sm:gap-2 pt-3 sm:pt-4 border-t">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
                    onClick={() => navigate(`/contacts/${contact.id}`)}
                  >
                    <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    <span className="hidden sm:inline">View</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(contact.id)}
                    className="text-red-600 hover:text-red-700 px-2 sm:px-3 py-1.5 sm:py-2"
                  >
                    <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Contacts;